/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, April 28th 2023, 7:32:31 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Organization } from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";
import { schemaBuilder, Many2OneUUIDPKBaseSchema, Many2ManyIntPKBaseSchema } from "lib/validation";

export interface IntegrationProvider {
  name: string;
  code: string;
  icon: string;
  description: string;
  actions: IntegrationAction[];
}

interface IntegrationAction {
  code: string;
  name: string;
  description: string;
}

export interface IntegrationConnection extends WritableModel<true> {
  name: string;
  integration_code: IntegrationProvider["code"];
  integration_actions: IntegrationProvider["actions"][number]["code"][];
  status: "active" | "inactive";
  readonly created_at?: string;
  readonly updated_at?: string;
  credentials_store: string;
  organization: Organization["id"];
  integration_task_templates: number[];
}

export interface IntegrationCredentialStore extends WritableModel<true> {
  name: string;
  credentials: string;
  organization: string;
}

type WriteOnlyIntegrationConnection = Omit<IntegrationConnection, "id" | "created_at" | "updated_at">;

export const IntegrationConnectionValidationSchema: schemaBuilder.SchemaOf<WriteOnlyIntegrationConnection> =
  schemaBuilder.object().shape({
    credentials_store: Many2OneUUIDPKBaseSchema("A valid credential store id is required.").required(
      "A valid credential store id is required."
    ),
    status: schemaBuilder.mixed().oneOf(["active", "inactive"]).required("Status is required"),
    name: schemaBuilder.string().required("Name is required."),
    integration_code: schemaBuilder.string().required("Integration code is required."),
    integration_actions: schemaBuilder
      .array(schemaBuilder.string())
      .required("Actions are required")
      .test({ test: (value) => value.length > 0, message: "At least one action is required." }),
    integration_task_templates: Many2ManyIntPKBaseSchema(),
    organization: schemaBuilder.string().uuid().required("Organization is Required"),
  });

type WriteOnlyCredentialStore = Omit<IntegrationCredentialStore, "id">;

export const CredentialStoreValidationSchema: schemaBuilder.SchemaOf<WriteOnlyCredentialStore> = schemaBuilder
  .object()
  .shape({
    name: schemaBuilder.string().required("Name is required."),
    credentials: schemaBuilder.mixed().required("Credentials are required."),
    organization: schemaBuilder.string().uuid().required("Organization is Required"),
  });
